import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSocket } from '../../../../contexts/SocketContext';
import api, { getLeadsWithRequirements } from '../../../../services/api';
import { useToast } from '@chakra-ui/react';
import { STAGE_ORDER, INITIAL_EXPANDED_STAGES, STAGE_IDS } from '../utils/NewKBConstants';

// Create context
const NewKBLeadContext = createContext();

// Lead provider component
export const NewKBLeadProvider = ({ children }) => {
  const { socket } = useSocket();
  const toast = useToast();

  // Core lead state
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Pagination state (simplified)
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingAllPages, setIsLoadingAllPages] = useState(false);
  
  // Keep track of expanded/collapsed stages
  const [expandedStages, setExpandedStages] = useState(INITIAL_EXPANDED_STAGES);
  
  // Track if closed leads have been loaded (deferred loading optimization)
  const [closedLeadsLoaded, setClosedLeadsLoaded] = useState(false);
  const [isLoadingClosedLeads, setIsLoadingClosedLeads] = useState(false);

  // Stage IDs to defer loading
  const DEFERRED_STAGES = ['stage_closed_lost'];

  // Function to fetch leads (excludes closed_lost by default for faster initial load)
  const fetchLeads = useCallback(async (page = 1, options = {}) => {
    try {
      setIsLoading(page === 1);
      
      // By default, exclude closed_lost for faster initial load
      const excludeStages = options.includeAll ? undefined : DEFERRED_STAGES.join(',');
      
      const response = await getLeadsWithRequirements({ 
        page,
        limit: 100,
        excludeStages
      });
      
      const newLeads = response.data.leads || [];
      const pagination = response.data.pagination || { total: 0, pages: 1, currentPage: 1 };
      
      // Update pagination info
      setCurrentPage(pagination.currentPage);
      const hasMorePages = pagination.currentPage < pagination.pages;
      
      // Log detailed pagination information to help with debugging
      console.log(`[NewKB] Pagination info:`, {
        currentPage: pagination.currentPage,
        totalPages: pagination.pages,
        totalLeads: pagination.total,
        hasMorePages
      });
      
      setHasMore(hasMorePages);
      
      if (page === 1) {
        // First page - replace all leads
        setLeads(newLeads);
      } else {
        // Additional page - append unique leads
        setLeads(prev => {
          // Create maps of existing leads by both _id and leadId for comprehensive deduplication
          const existingIdMap = new Map();
          const existingLeadIdMap = new Map();
          
          // Populate both maps for fastest possible lookups
          prev.forEach(lead => {
            if (lead._id) existingIdMap.set(lead._id, true);
            if (lead.leadId) existingLeadIdMap.set(lead.leadId, true);
          });
          
          // Enhanced deduplication checking both IDs
          const uniqueNewLeads = newLeads.filter(lead => {
            // Check both ID types for thoroughness
            const idExists = lead._id && existingIdMap.has(lead._id);
            const leadIdExists = lead.leadId && existingLeadIdMap.has(lead.leadId);
            
            // Check for both MongoDB ID and business ID
            return !idExists && !leadIdExists;
          });
          
          // Log detailed information about deduplication process
          console.log(`[NewKB] Page ${page}: ${newLeads.length} leads retrieved, ${uniqueNewLeads.length} unique`); 
          console.log(`[NewKB] Adding ${uniqueNewLeads.length} new leads, total will be ${prev.length + uniqueNewLeads.length}`);
          
          // Create a completely new array to ensure React detects the change
          return [...prev, ...uniqueNewLeads];
        });
      }
      
      // Return pagination info for chaining
      const result = { 
        hasMore: hasMorePages,
        currentPage: pagination.currentPage,
        totalPages: pagination.pages,
        totalLeads: pagination.total
      };
      
      // If we're on the last page, double check to make sure we got everything
      if (!hasMorePages && pagination.currentPage === pagination.pages) {
        console.log(`[NewKB] Reached the final page ${pagination.currentPage} of ${pagination.pages}`);
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast({
        title: 'Error fetching leads',
        description: error.response?.data?.error || 'Something went wrong',
        status: 'error',
        duration: 5000
      });
      return { hasMore: false };
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Function to load closed leads on demand (deferred loading)
  const loadClosedLeads = useCallback(async () => {
    if (closedLeadsLoaded || isLoadingClosedLeads) return;
    
    setIsLoadingClosedLeads(true);
    try {
      const response = await getLeadsWithRequirements({ 
        onlyStages: DEFERRED_STAGES.join(','),
        limit: 500 // Load all closed leads at once
      });
      
      const closedLeads = response.data.leads || [];
      
      if (closedLeads.length > 0) {
        setLeads(prev => {
          // Filter out any duplicates
          const existingIds = new Set(prev.map(l => l.leadId));
          const newClosedLeads = closedLeads.filter(l => !existingIds.has(l.leadId));
          return [...prev, ...newClosedLeads];
        });
        
        toast({
          title: `Loaded ${closedLeads.length} closed leads`,
          status: 'success',
          duration: 2000
        });
      }
      
      setClosedLeadsLoaded(true);
    } catch (error) {
      console.error('Error loading closed leads:', error);
      toast({
        title: 'Error loading closed leads',
        status: 'error',
        duration: 3000
      });
    } finally {
      setIsLoadingClosedLeads(false);
    }
  }, [closedLeadsLoaded, isLoadingClosedLeads, toast]);

  // Function to load more leads
  const loadMoreLeads = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchLeads(currentPage + 1);
    }
  }, [fetchLeads, currentPage, hasMore, isLoading]);

  // Function to load all leads sequentially with retry capability
  const loadAllLeads = useCallback(async () => {
    if (isLoadingAllPages || !hasMore) return;
    
    setIsLoadingAllPages(true);
    console.log('[NewKB] Starting sequential loading of all pages');
    
    const maxRetries = 3; // Maximum number of retries for each page
    const delayBetweenPages = 300; // Delay between pages (ms) - optimized
    const retryDelay = 1000; // Delay before retrying a failed page (ms)
    
    // Start with the current leads array and build upon it instead of setting state each time
    const startingLeadCount = leads.length;
    console.log(`[NewKB] Starting with ${startingLeadCount} leads already loaded`);
    
    // Use a local accumulator to avoid multiple state updates
    let accumulatedLeads = [...leads];
    
    // Maps for deduplication
    const existingIdMap = new Map();
    const existingLeadIdMap = new Map();
    
    // Initialize maps with existing leads
    accumulatedLeads.forEach(lead => {
      if (lead._id) existingIdMap.set(lead._id, true);
      if (lead.leadId) existingLeadIdMap.set(lead.leadId, true);
    });
    
    try {
      let pageToLoad = currentPage + 1;
      let hasMorePages = true;
      let failedPages = [];
      
      // First pass: try to load all pages
      while (hasMorePages) {
        console.log(`[NewKB] Loading page ${pageToLoad}...`);
        
        let retryCount = 0;
        let pageLoaded = false;
        
        // Try loading this page up to maxRetries times
        while (!pageLoaded && retryCount < maxRetries) {
          try {
            // Load the next page WITHOUT updating state - we'll collect them all first
            const response = await getLeadsWithRequirements({ 
              page: pageToLoad,
              limit: 100 // Increased from 50 to 100 to load more leads per page
            });
            
            const newLeads = response.data.leads || [];
            const pagination = response.data.pagination || { total: 0, pages: 1, currentPage: 1 };
            
            // Check if there are more pages without updating state
            const morePages = pagination.currentPage < pagination.pages;
            console.log(`[NewKB] Page ${pageToLoad} loaded, hasMore=${morePages}, totalPages=${pagination.pages || '?'}`);
            
            // Add only unique new leads to our accumulator
            const uniqueNewLeads = newLeads.filter(lead => {
              const idExists = lead._id && existingIdMap.has(lead._id);
              const leadIdExists = lead.leadId && existingLeadIdMap.has(lead.leadId);
              return !idExists && !leadIdExists;
            });
            
            // Add the unique leads to our accumulated collection without a state update
            uniqueNewLeads.forEach(lead => {
              if (lead._id) existingIdMap.set(lead._id, true);
              if (lead.leadId) existingLeadIdMap.set(lead.leadId, true);
              accumulatedLeads.push(lead);
            });
            
            console.log(`[NewKB] Added ${uniqueNewLeads.length} leads from page ${pageToLoad}, total now: ${accumulatedLeads.length}`);
            
            // CRITICAL: Only stop when we're explicitly told there are no more pages
            hasMorePages = morePages;
            pageLoaded = true;
            pageToLoad += 1;
            
            // Update state once every few pages to show progress without too many re-renders
            if (pageToLoad % 3 === 0 || !morePages) {
              setLeads([...accumulatedLeads]);
              setCurrentPage(pagination.currentPage);
              setHasMore(morePages);
            }
            
            // Successful load - add delay before next page
            if (hasMorePages) {
              await new Promise(resolve => setTimeout(resolve, delayBetweenPages));
            }
          } catch (error) {
            retryCount += 1;
            console.error(`[NewKB] Error loading page ${pageToLoad} (attempt ${retryCount}/${maxRetries}):`, error);
            
            if (retryCount < maxRetries) {
              // Wait before retrying
              console.log(`[NewKB] Retrying page ${pageToLoad} in ${retryDelay}ms...`);
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            } else {
              // Max retries reached for this page, mark as failed and continue
              console.log(`[NewKB] Failed to load page ${pageToLoad} after ${maxRetries} attempts`);
              failedPages.push(pageToLoad);
              pageToLoad += 1;
              
              // Don't let a single failed page stop the entire process
              // We will continue loading until the API tells us there are no more pages
              // Removing the artificial limit to ensure we get ALL leads
            }
          }
        }
      }
      
      // Make one final state update with all leads
      setLeads(accumulatedLeads);
      
      // Show appropriate message based on whether all pages loaded
      console.log(`[NewKB] Final lead count: ${accumulatedLeads.length} (started with ${startingLeadCount})`);
      
      // Small delay to ensure state is up to date before showing toast
      setTimeout(() => {
        if (failedPages.length === 0) {
          console.log('[NewKB] Successfully loaded all pages');
          toast({
            title: `All ${accumulatedLeads.length} leads loaded`,
            description: 'The complete dataset is now available',
            status: 'success',
            duration: 3000
          });
        } else {
          console.log('[NewKB] Finished with some failed pages:', failedPages);
          toast({
            title: 'Some leads could not be loaded',
            description: `Failed to load ${failedPages.length} page(s). Try again or refresh the page.`,
            status: 'warning',
            duration: 5000,
            isClosable: true
          });
        }
      }, 500);
    } catch (error) {
      console.error('[NewKB] Error during sequential loading:', error);
      toast({
        title: 'Error loading leads',
        description: 'Some leads may not have been loaded completely',
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    } finally {
      // Always clear loading state
      setIsLoadingAllPages(false);
    }
  }, [fetchLeads, currentPage, hasMore, isLoadingAllPages, toast]);

  // Use hardcoded stages instead of fetching from API
  const loadHardcodedStages = useCallback(() => {
    // Create hardcoded stage objects with proper structure
    const hardcodedStages = [
      {
        _id: STAGE_IDS.LEAD_IN,
        name: 'Lead In',
        value: STAGE_IDS.LEAD_IN,
        color: 'blue.500',
      },
      {
        _id: STAGE_IDS.NURTURE,
        name: 'Nurture',
        value: STAGE_IDS.NURTURE,
        color: 'orange.400',
      },
      {
        _id: STAGE_IDS.PROPOSAL_NEEDED,
        name: 'Proposal Needed',
        value: STAGE_IDS.PROPOSAL_NEEDED,
        color: 'purple.400',
      },
      {
        _id: STAGE_IDS.FIX_MEETING,
        name: 'Fix Meeting',
        value: STAGE_IDS.FIX_MEETING,
        color: 'pink.400',
      },
      {
        _id: STAGE_IDS.CLOSED_WON,
        name: 'Closed Won',
        value: STAGE_IDS.CLOSED_WON,
        color: 'green.500',
      },
      {
        _id: STAGE_IDS.CLOSED_LOST,
        name: 'Closed Lost',
        value: STAGE_IDS.CLOSED_LOST,
        color: 'red.500',
      }
    ];
    
    // Ensure stages are in the correct order
    const orderedStages = hardcodedStages.sort((a, b) => {
      const indexA = STAGE_ORDER.indexOf(a._id);
      const indexB = STAGE_ORDER.indexOf(b._id);
      return indexA - indexB;
    });
    
    setStages(orderedStages);
    console.log('[NewKB] Using hardcoded stages:', orderedStages);
  }, []);

  // Initial data loading
  useEffect(() => {
    loadHardcodedStages();
    fetchLeads(1);
    // Force reset to initial expanded states to ensure correct loading
    setExpandedStages(INITIAL_EXPANDED_STAGES);
  }, [loadHardcodedStages, fetchLeads]);
  
  // Track if this device has already performed initial load
  const initialLoadCompleteRef = useRef(false);
  
  // Auto-load all pages when first page is loaded - but only once per session
  useEffect(() => {
    // Only auto-load if this is the first time and we haven't completed initial load yet
    if (currentPage === 1 && hasMore && !isLoading && !isLoadingAllPages && !initialLoadCompleteRef.current) {
      console.log('[NewKB] First page loaded, starting auto-loading of remaining pages');
      // Mark as completed so we don't trigger again from socket events
      initialLoadCompleteRef.current = true;
      // Small delay before loading all pages to ensure the UI is responsive
      setTimeout(() => {
        loadAllLeads();
      }, 500);
    }
  }, [currentPage, hasMore, isLoading, isLoadingAllPages, loadAllLeads]);

  // Track the local device socket ID to distinguish local vs remote events
  const localSocketIdRef = useRef(null);

  // Handle socket events
  useEffect(() => {
    if (!socket) return;
    
    // Store the local socket ID for identifying local vs remote events
    localSocketIdRef.current = socket.id;
    console.log('[NewKB] Local socket ID stored:', socket.id);

    // Handle stage changes
    const handleStageChanged = (data) => {
      const leadId = data.leadId;
      const newStage = data.stage || data.newStage; // Handle both formats
      
      // Only proceed if we have a valid business leadId
      if (!leadId) {
        console.warn('[NewKB] Received stage change without business leadId');
        return;
      }
      
      console.log('[NewKB] Received stage change via socket:', { 
        leadId, 
        newStage,
        stageName: data.stageName,
        timestamp: new Date().toLocaleTimeString()
      });
      
      // Stage update toast removed to reduce UI spam - updates are reflected in the Kanban board
      
      
      setLeads(prev => {
        // Only match on business leadId (SUM-*)
        const leadIndex = prev.findIndex(l => l.leadId === leadId);
        
        if (leadIndex !== -1) {
          console.log('[NewKB] Updating stage for lead at index:', leadIndex);
          const updated = [...prev];
          
          // CRITICAL FIX: Use the previous lead as the base to preserve ALL properties
          const prevLead = prev[leadIndex];
          
          console.log('[NewKB] 🔍 Found lead to update at index:', leadIndex, 'Current lead data:', {
            name: prevLead.brideAndGroomName,
            stage: prevLead.stage,
            id: prevLead.leadId
          });
          
          // CRITICAL: Check if newStage is an object instead of a string ID
          let stageValue = newStage;
          if (typeof stageValue === 'object' && stageValue !== null) {
            console.log('[NewKB] ⚠️ Stage in socket event is an object:', stageValue);
            stageValue = stageValue._id || stageValue.id || stageValue;
            console.log('[NewKB] 🔧 Extracted stage ID from object:', stageValue);
          }
          
          // Create new object using prevLead as the base to ensure NO properties are lost
          updated[leadIndex] = { 
            ...prevLead,  // Start with ALL existing properties from the original lead
            // Only update the stage as a string ID, not an object
            stage: typeof stageValue === 'string' ? stageValue : prevLead.stage,
            _updateKey: Date.now(), // Force React to detect this as a new object
            _lastUpdated: Date.now()
          };
          
          // Verify ALL critical properties are preserved
          console.log('[NewKB] ✅ Updated lead with new stage. Verification:', {
            leadId: updated[leadIndex].leadId,
            stage: updated[leadIndex].stage,
            name: updated[leadIndex].brideAndGroomName,
            propertyCount: Object.keys(updated[leadIndex]).length,
            originalPropertyCount: Object.keys(prevLead).length
          });
          
          return updated;
        }
        
        console.log('[NewKB] Lead not found for stage update:', leadId);
        return prev;
      });
    };

    // Handle lead updates
    const handleLeadUpdated = (data) => {
      // Check if this is a system event that should be ignored for reloading
      if (data._systemEvent === 'initialLoad') {
        console.log('[NewKB] Ignoring initial load system event');
        return;
      }
      
      // Check if this update came from the same device (by checking socket ID)
      const sourceSocketId = data._sourceSocketId;
      if (sourceSocketId && sourceSocketId === localSocketIdRef.current) {
        console.log('[NewKB] Ignoring update from same device:', sourceSocketId);
        return;
      }
      
      // VERBOSE LOGGING: Log the exact data received
      console.log('[NewKB] 📥 RECEIVED SOCKET EVENT lead:updated:', JSON.stringify(data));
      
      // Handle both formats: direct lead object or {leadId, lead} format
      const updatedLead = data.lead || data;
      // Always use the business leadId (SUM-*) first, fall back to _id only if necessary
      const businessLeadId = updatedLead.leadId;
      
      if (!businessLeadId) {
        console.warn('[NewKB] ⚠️ Received lead update without business leadId', updatedLead);
        return; // Skip updates without a proper business leadId
      }
      
      console.log('[NewKB] 🔄 Processing lead update:', {
        leadId: businessLeadId,
        name: updatedLead?.brideAndGroomName,
        nextFollowUpTime: updatedLead?.nextFollowUpTime,
        callCount: updatedLead?.callCount,
        eventTimestamp: new Date().toLocaleTimeString()
      });
      
      // Add a timestamp to force re-rendering and use completely new object reference
      const updatedLeadWithNewRef = { 
        ...updatedLead, 
        callCount: updatedLead.callCount !== undefined ? Number(updatedLead.callCount) : updatedLead.callCount,
        // Add timestamp to ensure the lead object is always a new reference
        _lastUpdated: Date.now(),
        // Add an explicit key for detecting changes
        _updateKey: Math.random().toString(36).substring(2, 15)
      };
      
      console.log('[NewKB] 🆕 Created new lead object with unique references:', {
        leadId: businessLeadId,
        updateKey: updatedLeadWithNewRef._updateKey,
        timeStamp: new Date().toLocaleTimeString(),
        name: updatedLeadWithNewRef.brideAndGroomName, 
        callCount: updatedLeadWithNewRef.callCount
      });

      // DEBUG: Log the state before updating
      console.log('[NewKB] 📊 Current leads state count:', leads.length);
      
      setLeads(prev => {
        // Only match on business leadId (SUM-*)
        const leadIndex = prev.findIndex(l => l.leadId === businessLeadId);
        
        console.log('[NewKB] 🔍 Searching for lead:', {
          searchingFor: businessLeadId,
          foundAt: leadIndex,
          totalLeadsInState: prev.length
        });
        
        if (leadIndex !== -1) {
          console.log('[NewKB] ✅ Found lead in array at index:', leadIndex);
          
          // Compare old and new for debugging
          const oldLead = prev[leadIndex];
          console.log('[NewKB] ↔️ Lead values comparison:', {
            oldName: oldLead.brideAndGroomName,
            newName: updatedLeadWithNewRef.brideAndGroomName,
            oldCallCount: oldLead.callCount,
            newCallCount: updatedLeadWithNewRef.callCount,
            isDifferent: oldLead.brideAndGroomName !== updatedLeadWithNewRef.brideAndGroomName
          });
          
          // Create an entirely new array to ensure React detects the change
          const updated = [...prev];
          
          // Create a completely new object with NO shared references to force re-render
          // First, preserve important counts that might not be included in the update
          const prevLead = prev[leadIndex];
          const preservedCounts = {
            callCount: prevLead.callCount || 0,
            commentCount: prevLead.commentCount || 0,
            taskCount: prevLead.taskCount || 0
          };
          
          console.log('[NewKB] Preserving counts during update:', preservedCounts);
          
          // We already have the previous lead properties
          
          // Create base updated lead by merging the update over the previous lead
          // Rather than using JSON parsing which can cause issues
          const baseUpdated = {
            ...prevLead,  // Start with all existing properties including stage
            ...updatedLeadWithNewRef, // Apply updates over it
          };
          
          // EXPLICITLY ensure stage and other critical fields are preserved
          if (!baseUpdated.stage && prevLead.stage) {
            console.log('[NewKB] ⚠️ Stage was missing in update! Preserving original stage:', prevLead.stage);
            baseUpdated.stage = prevLead.stage;
          }
          
          // Log the base updated object
          console.log('[NewKB] 📝 Base updated lead (with stage preservation):', {
            name: baseUpdated.brideAndGroomName,
            stage: baseUpdated.stage,
            hasCallCount: 'callCount' in baseUpdated,
            callCount: baseUpdated.callCount
          });
          
          // Create final object with preserved counts applied LAST
          updated[leadIndex] = {
            ...baseUpdated,  // Base updated lead with preserved properties
            // EXPLICITLY apply correct counts as the final step
            callCount: 'callCount' in updatedLeadWithNewRef ? Number(updatedLeadWithNewRef.callCount) : preservedCounts.callCount,
            commentCount: 'commentCount' in updatedLeadWithNewRef ? updatedLeadWithNewRef.commentCount : preservedCounts.commentCount,
            taskCount: 'taskCount' in updatedLeadWithNewRef ? updatedLeadWithNewRef.taskCount : preservedCounts.taskCount,
            // CRITICAL: Make sure stage is always present
            stage: baseUpdated.stage || prevLead.stage,
            // CRITICAL: Ensure leadId is preserved for drag functionality
            leadId: prevLead.leadId,
            // Add update markers to ensure React detects changes
            _lastUpdated: Date.now(),
            _updateKey: Math.random().toString(36).substring(2, 15)
          };
          
          // Verify our final object has all critical fields
          console.log('[NewKB] ✅ FINAL updated lead with preserved fields:', {
            name: updated[leadIndex].brideAndGroomName,
            stage: updated[leadIndex].stage, // Log the stage to verify it's preserved
            callCount: updated[leadIndex].callCount,
            commentCount: updated[leadIndex].commentCount,
            taskCount: updated[leadIndex].taskCount,
            updateKey: updated[leadIndex]._updateKey
          });

          console.log('[NewKB] 📤 State update prepared with updated lead');
          return updated;
        }
        
        console.log('[NewKB] ❌ Lead not found in array, no update performed');
        return prev;
      });
      
      // Log the update being complete - this will help confirm the state was updated
      setTimeout(() => {
        console.log('[NewKB] ⏱️ AFTER STATE UPDATE: Checking if lead update is reflected in state');
        const updatedLeadInState = leads.find(l => l.leadId === businessLeadId);
        if (updatedLeadInState) {
          console.log('[NewKB] 🔄 Lead in state after update:', {
            leadId: updatedLeadInState.leadId,
            name: updatedLeadInState.brideAndGroomName,
            updateKey: updatedLeadInState._updateKey
          });
        } else {
          console.log('[NewKB] ⚠️ Could not find updated lead in state');
        }
      }, 100); // Small delay to let the state update
    };

    // Handle new leads
    const handleNewLead = (data) => {
      // Handle both formats: direct lead object or {leadId, lead} format
      const newLead = data.lead || data;
      // Always use the business leadId (SUM-*) first, fall back to _id only if necessary
      const businessLeadId = newLead.leadId;
      
      if (!businessLeadId) {
        console.warn('[NewKB] Received new lead without business leadId', newLead);
        return; // Skip leads without a proper business leadId
      }
      
      console.log('[NewKB] Received new lead:', { leadId: businessLeadId });
      
      setLeads(prev => {
        // Only match on business leadId (SUM-*)
        const exists = prev.some(l => l.leadId === businessLeadId);
        if (!exists) {
          console.log('[NewKB] Adding new lead to array');
          return [newLead, ...prev];
        }
        return prev;
      });
    };

    // Subscribe to events
    socket.on('lead:stageChanged', handleStageChanged);
    socket.on('lead:stage-updated', handleStageChanged); // Add the new event format
    socket.on('lead:updated', handleLeadUpdated);
    socket.on('lead:created', handleNewLead);
    socket.on('call:added', handleLeadUpdated);
    socket.on('callCount:updated', handleLeadUpdated);
    
    // Log connection status for debugging
    console.log('[NewKB] Socket event listeners registered:', {
      socketConnected: Boolean(socket.connected),
      socketId: socket.id,
      registerTime: new Date().toLocaleTimeString()
    });

    // Cleanup
    return () => {
      socket.off('lead:stageChanged', handleStageChanged);
      socket.off('lead:stage-updated', handleStageChanged); // Remove the new event format
      socket.off('lead:updated', handleLeadUpdated);
      socket.off('lead:created', handleNewLead);
      socket.off('call:added', handleLeadUpdated);
      socket.off('callCount:updated', handleLeadUpdated);
    };
  }, [socket]);

  // Toggle stage expansion
  const toggleStageExpansion = useCallback((stageId) => {
    setExpandedStages(prev => ({
      ...prev,
      [stageId]: !prev[stageId]
    }));
  }, []);

  // Group leads by stage
  const getLeadsByStage = useCallback((stageId) => {
    // Filter leads by stage ID, handling both string IDs and object format
    const filteredLeads = leads.filter(lead => {
      // Handle stage as either string ID or object with _id property
      const leadStageId = typeof lead.stage === 'object' ? lead.stage?._id : lead.stage;
      const match = leadStageId === stageId;
      return match;
    });
    
    return filteredLeads;
  }, [leads]);

  // Function to update a lead locally without fetching all leads again
  const updateLeadLocally = useCallback((updatedLead) => {
    if (!updatedLead) {
      console.error('[NewKB] Cannot update lead locally: Invalid lead data', updatedLead);
      return;
    }

    // Prioritize business leadId if available
    const businessLeadId = updatedLead.leadId;
    const mongoId = updatedLead._id;
    
    if (!businessLeadId && !mongoId) {
      console.error('[NewKB] Cannot update lead locally: No ID found', updatedLead);
      return;
    }

    console.log('[NewKB] Updating lead locally:', { 
      businessLeadId, 
      mongoId, 
      hasBusinessId: Boolean(businessLeadId)
    });
    
    setLeads(prev => {
      // Find the lead by ID, prioritizing business leadId matches
      let leadIndex = -1;
      
      // First try to find by business leadId
      if (businessLeadId) {
        leadIndex = prev.findIndex(l => l.leadId === businessLeadId);
      }
      
      // If not found and we have mongoId, try that
      if (leadIndex === -1 && mongoId) {
        leadIndex = prev.findIndex(l => l._id === mongoId);
      }
      
      if (leadIndex >= 0) {
        console.log('[NewKB] Found lead to update at index:', leadIndex);
        
        // Create an entirely new array to ensure React detects the change
        const updated = [...prev];
        
        // CRITICAL FIX: Start with the COMPLETE previous lead and only update specific fields
        const prevLead = prev[leadIndex];
        
        console.log('[NewKB] 🔍 Lead before update:', {
          name: prevLead.brideAndGroomName,
          stage: prevLead.stage + ' → ' + updatedLead.stage,
          id: prevLead.leadId,
          propertyCount: Object.keys(prevLead).length
        });
        
        // CRITICAL: Check if stage is an object and extract just the ID if needed
        let stageValue = updatedLead.stage;
        
        // If stage is an object with _id, use just the ID string
        if (typeof stageValue === 'object' && stageValue !== null) {
          console.log('[NewKB] ⚠️ Stage is an object instead of string ID:', stageValue);
          stageValue = stageValue._id || stageValue.id || stageValue;
          console.log('[NewKB] 🔧 Fixed stage value to:', stageValue);
        }
        
        // ONLY extract fields we want to update from the update payload
        const fieldsToUpdate = {
          // Only copy the stage as a string ID, not an object
          stage: typeof stageValue === 'string' ? stageValue : (prevLead.stage || ''),
          // Force React to detect this as a new object
          _updateKey: Date.now(),
          _lastUpdated: Date.now()
        };
        
        // Create final object by starting with ALL fields from the previous lead
        // and ONLY changing the fields we explicitly want to update
        updated[leadIndex] = {
          ...prevLead,        // Keep ALL original properties intact
          ...fieldsToUpdate,  // Only apply specific changes
          // EXTRA SAFETY: Always ensure the leadId is preserved
          leadId: prevLead.leadId || updatedLead.leadId
        };
        
        // Verify the lead still has all its properties after the update
        console.log('[NewKB] ✅ Lead after update:', {
          name: updated[leadIndex].brideAndGroomName,
          stage: updated[leadIndex].stage,
          id: updated[leadIndex].leadId,
          propertyCount: Object.keys(updated[leadIndex]).length
        });
        
        return updated;
      }
      
      console.log('[NewKB] Lead not found for local update:', { businessLeadId, mongoId });
      return prev;
    });
  }, []);

  // Context value
  const value = {
    leads,
    stages,
    isLoading,
    hasMore,
    isLoadingAllPages,
    expandedStages,
    fetchLeads,
    loadMoreLeads,
    loadAllLeads,
    loadClosedLeads, // Deferred loading for Closed Lost leads
    closedLeadsLoaded,
    isLoadingClosedLeads,
    toggleStageExpansion,
    getLeadsByStage,
    socket,
    updateLeadLocally,
  };

  return (
    <NewKBLeadContext.Provider value={value}>
      {children}
    </NewKBLeadContext.Provider>
  );
};

// Custom hook to use the lead context
export const useNewKBLead = () => {
  const context = useContext(NewKBLeadContext);
  if (context === undefined) {
    throw new Error('useNewKBLead must be used within a NewKBLeadProvider');
  }
  return context;
};
